import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Divider, Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { Check, X } from 'lucide-react'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_EXPIRATION_NOTIFICATION_CONFIGURATIONS,
  SAVE_EXPIRATION_NOTIFICATION_SETTINGS,
} from 'src/graphql/expirationNotificationConfigurations.graphql'
import { GET_COMPANY } from 'src/graphql/companies.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

import BackfillConfirmationModal from './BackfillConfirmationModal'

import FieldLabel from './FieldLabel'
import NotificationConfigTable, {
  type NotificationConfigRow,
} from './NotificationConfigTable'

interface ExpirationNotificationsCardProps {
  companyData: {
    companyId: string
    expirationTrackingEnabled: boolean
  }
  initialOpened?: boolean
}

const TOGGLE_OPTIONS = [
  { value: 'yes', label: 'Enabled' },
  { value: 'no', label: 'Disabled' },
] as const

const SYSTEM_DEFAULTS: NotificationConfigRow[] = [
  { category: null, offsetDays: 90, actionType: 'SMS', recipient: 'APPLICANT' },
  { category: null, offsetDays: 60, actionType: 'EMAIL', recipient: 'APPLICANT' },
  { category: null, offsetDays: 30, actionType: 'SMS', recipient: 'APPLICANT' },
  { category: null, offsetDays: 14, actionType: 'SMS', recipient: 'APPLICANT' },
  { category: null, offsetDays: 1, actionType: 'EMAIL', recipient: 'APPLICANT' },
]

const ExpirationNotificationsCard = ({
  companyData,
  initialOpened = false,
}: ExpirationNotificationsCardProps) => {
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)
  const [backfillModalOpened, backfillModal] = useDisclosure(false)

  const { data: configsData, loading: configsLoading } = useQuery(
    GET_EXPIRATION_NOTIFICATION_CONFIGURATIONS
  )

  const savedConfigs: NotificationConfigRow[] =
    configsData?.expirationNotificationConfigurations?.length > 0
      ? configsData.expirationNotificationConfigurations.map((c) => ({
          id: c.expirationNotificationConfigurationId,
          category: c.category,
          offsetDays: c.offsetDays,
          actionType: c.actionType,
          recipient: c.recipient,
        }))
      : [...SYSTEM_DEFAULTS]

  const form = useForm({
    initialValues: {
      enabled: companyData.expirationTrackingEnabled,
      configs: savedConfigs,
    },
  })

  // Sync form with fetched data when not editing
  useEffect(() => {
    if (!editing) {
      form.setFieldValue('enabled', companyData.expirationTrackingEnabled)
      form.setFieldValue('configs', savedConfigs)
    }
  }, [companyData.expirationTrackingEnabled, configsData]) // eslint-disable-line react-hooks/exhaustive-deps

  const [saveSettings, { loading: saving }] = useMutation(
    SAVE_EXPIRATION_NOTIFICATION_SETTINGS,
    {
      onCompleted: () => {
        toast('Expiration tracking settings saved', 'success')
        setEditing(false)
      },
      onError: (error) => {
        toast(error.message || 'Failed to save settings', 'error')
      },
      refetchQueries: [
        { query: GET_COMPANY, variables: { id: companyData.companyId } },
        { query: GET_EXPIRATION_NOTIFICATION_CONFIGURATIONS },
      ],
      awaitRefetchQueries: true,
    }
  )

  const handleSave = async () => {
    const { enabled, configs } = form.values
    await saveSettings({
      variables: {
        input: {
          expirationTrackingEnabled: enabled,
          configs: configs.map((c) => ({
            expirationNotificationConfigurationId: c.id || undefined,
            category: c.category,
            offsetDays: c.offsetDays,
            actionType: c.actionType,
            recipient: c.recipient,
          })),
        },
      },
    })
  }

  const handleCancel = () => {
    form.setValues({
      enabled: companyData.expirationTrackingEnabled,
      configs: savedConfigs,
    })
    setEditing(false)
  }

  const handleUpdateRow = (
    index: number,
    field: keyof NotificationConfigRow,
    value: string | number | null
  ) => {
    form.setFieldValue(`configs.${index}.${field}`, value)
  }

  const handleRemoveRow = (index: number) => {
    form.removeListItem('configs', index)
  }

  const handleAddRow = () => {
    form.insertListItem('configs', {
      category: null,
      offsetDays: 7,
      actionType: 'SMS',
      recipient: 'APPLICANT',
    })
  }

  const handleBackfill = () => {
    backfillModal.open()
  }

  const handleFillDefaults = () => {
    form.setFieldValue('configs', [...SYSTEM_DEFAULTS])
  }

  const { enabled, configs } = form.values
  const canSave = !enabled || configs.length > 0

  const enableLabel = (
    <FieldLabel
      label="Expiration Tracking"
      tooltip="When enabled, the system tracks document expirations and sends automated reminders to applicants via SMS and email."
    />
  )

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      {/* Header */}
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${!opened && '-rotate-90'
              } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Expirations
        {!editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              setEditing(true)
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {editing && (
          <div
            className="ml-auto flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <DNButton
              variant="transparent"
              onClick={handleSave}
              disabled={!canSave || saving}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={handleCancel}
              disabled={saving}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>

      {/* Content */}
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-3">
          {/* Toggle + Backfill */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={enableLabel}
                  value={enabled ? 'yes' : 'no'}
                  onChange={(val) =>
                    form.setFieldValue('enabled', val === 'yes')
                  }
                  data={TOGGLE_OPTIONS}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                    input: 'text-sm',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={enableLabel}
                  value={enabled ? 'Enabled' : 'Disabled'}
                />
              )}
            </div>
            <div className="col-span-3">
              <FieldLabel
                label="Backfill"
                tooltip="Creates expiration trackers for all existing HIRED applicants based on the current notification schedule. Requires expiration tracking to be enabled and saved."
              />
              <div className="mt-1 w-fit">
                <DNButton
                  variant="outline"
                  disabled={
                    !companyData.expirationTrackingEnabled || editing
                  }
                  onClick={handleBackfill}
                  className="text-xs"
                >
                  Run
                </DNButton>
              </div>
            </div>
          </div>

          <Divider />

          {/* Notification Schedule Section */}
          <div>
            <div className="mb-1 text-sm font-semibold text-doubleNickel-gray-900">
              Notification Schedule
            </div>
            <div className="mb-2 text-xs text-doubleNickel-gray-500">
              Notifications are automatically created with each expiration
              tracker based on this schedule.
            </div>
          </div>

          {/* Hint when disabled */}
          {!enabled && (
            <StyledAlert
              description="Enable expiration tracking to manage notifications."
              variant="brand"
            />
          )}

          {/* Config table — faded when disabled */}
          <div
            className={`transition-opacity duration-300 ${!enabled ? 'pointer-events-none opacity-40' : ''
              }`}
          >
            <NotificationConfigTable
              configs={configs}
              editing={editing}
              onUpdate={handleUpdateRow}
              onRemove={handleRemoveRow}
              onAdd={handleAddRow}
              onFillDefaults={handleFillDefaults}
            />

            {/* Validation hint */}
            {editing && enabled && configs.length === 0 && (
              <div className="mt-2 text-xs text-doubleNickel-error-500">
                At least one notification is required when tracking is
                enabled.
              </div>
            )}
          </div>
        </div>
      </Collapse>

      <BackfillConfirmationModal
        isOpen={backfillModalOpened}
        onClose={backfillModal.close}
      />
    </div>
  )
}

export default ExpirationNotificationsCard
